/**
 * Sticker maker (WhatsApp-style) — port of the native app's
 * lib/src/ui/sticker_maker.dart. A photo → cut out the subject → move / zoom,
 * erase or restore by hand, draw, add text and emoji, pick a shape and an
 * outline → a 512×512 PNG with transparency. Everything happens on the device
 * (stickers/cutout.ts runs in a Web Worker); nothing is uploaded.
 *
 * Geometry is kept in "sticker units": the canvas is STICKER_SIDE (512) wide
 * whatever its size on screen, so export is the same drawing at 1:1. Phone
 * sizes (measured on a ~400 px canvas) are scaled by U = 1.28.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from "react";
import { IconEmoji } from "@veto/ui";
import { LayerDialog, openLayer } from "./StickerLayer";
import {
  CUTOUT_FAILED_MESSAGE,
  STICKER_SIDE as S,
  clamp,
  cutoutLooksWrong,
  eraseDisc,
  fitToSubject,
  hasTransparency,
} from "../stickers/cutout";
import { runCutout } from "../stickers/cutoutRunner";
import { OPEN_FAILED_MESSAGE, encodeSticker, loadPicture } from "../stickers/stickerImage";

type Mode = "move" | "erase" | "restore" | "draw";
type Shape = "free" | "circle" | "rounded";

interface Item {
  text: string;
  emoji: boolean;
  color: string;
  /** Centre, sticker units. */
  x: number;
  y: number;
  scale: number;
}

interface Stroke {
  color: string;
  width: number;
  /** Flat x,y pairs in sticker units. */
  points: number[];
}

interface Pic {
  w: number;
  h: number;
  rgba: Uint8ClampedArray<ArrayBuffer>;
  original: Uint8ClampedArray<ArrayBuffer>;
  base: HTMLCanvasElement;
  silhouette: HTMLCanvasElement;
}

interface View {
  offX: number;
  offY: number;
  scale: number;
}

type Gesture =
  | { kind: "none" }
  | { kind: "pan"; lastX: number; lastY: number; startX: number; startY: number; moved: boolean }
  | { kind: "item"; index: number; lastX: number; lastY: number; startX: number; startY: number; moved: boolean }
  | { kind: "draw"; stroke: Stroke }
  | { kind: "paint"; lastX: number; lastY: number }
  | { kind: "pinch"; target: number | "picture"; startDist: number; startScale: number; midX: number; midY: number };

const U = 1.28;
const BRUSH_MIN = Math.round(6 * U);
const BRUSH_MAX = Math.round(60 * U);
const BRUSH_DEFAULT = Math.round(22 * U);
const OUTLINE_R = 5 * U;
const TEXT_FONT = 34 * U;
const EMOJI_FONT = 64 * U;
const ITEM_PAD = 4 * U;
const PICTURE_SCALE: [number, number] = [0.3, 4];
const ITEM_SCALE: [number, number] = [0.4, 4];

const INKS: { color: string; name: string }[] = [
  { color: "#FFFFFF", name: "White" },
  { color: "#000000", name: "Black" },
  { color: "#FFD43B", name: "Yellow" },
  { color: "#FF5A5F", name: "Red" },
  { color: "#4DA3FF", name: "Blue" },
  { color: "#3DDC97", name: "Green" },
];

const EMOJI = ["😂", "❤️", "😍", "🔥", "👍", "🙏", "😎", "🥳", "😭", "😮", "🤔", "😡", "👏", "💯", "✨", "🎉", "💀", "🙈", "🤝", "💪", "⭐", "🌈", "☕", "🍕"];

const TEXT_FONT_STACK = '"Avenir Next", "Segoe UI Variable", "Segoe UI", ui-sans-serif, system-ui, sans-serif';
const EMOJI_FONT_STACK = '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", "Twemoji Mozilla", sans-serif';

const SHAPE_LABEL: Record<Shape, string> = { free: "Shape", circle: "Circle", rounded: "Rounded" };
const NEXT_SHAPE: Record<Shape, Shape> = { free: "circle", circle: "rounded", rounded: "free" };

/** Opens the editor on `source` (photo or an existing sticker). Resolves with the sticker, or null. */
export function openStickerMaker(source: Blob, opts: { editing?: boolean } = {}): Promise<Blob | null> {
  return openLayer<Blob | null>(
    (close) => (
      <StickerMaker
        source={source}
        editing={!!opts.editing}
        onCancel={() => close(null)}
        onDone={(png) => close(png)}
      />
    ),
    null,
  );
}

/* ------------------------------- drawing --------------------------------- */

function luminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
}

function itemFont(it: Item): { size: number; font: string } {
  const size = (it.emoji ? EMOJI_FONT : TEXT_FONT) * it.scale;
  return { size, font: it.emoji ? `${size}px ${EMOJI_FONT_STACK}` : `900 ${size}px ${TEXT_FONT_STACK}` };
}

function itemBounds(ctx: CanvasRenderingContext2D, it: Item) {
  const { size, font } = itemFont(it);
  ctx.save();
  ctx.font = font;
  const w = ctx.measureText(it.text).width + (it.emoji ? 0 : size / 7);
  ctx.restore();
  const h = size * 1.2;
  return { x: it.x - w / 2 - ITEM_PAD, y: it.y - h / 2 - ITEM_PAD, w: w + ITEM_PAD * 2, h: h + ITEM_PAD * 2 };
}

function roundedPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function destRect(pic: Pic, view: View) {
  const fit = Math.min(S / pic.w, S / pic.h) * view.scale;
  const w = pic.w * fit;
  const h = pic.h * fit;
  return { x: S / 2 + view.offX - w / 2, y: S / 2 + view.offY - h / 2, w, h };
}

interface Scene {
  pic: Pic;
  view: View;
  items: Item[];
  strokes: Stroke[];
  shape: Shape;
  outline: boolean;
  cut: boolean;
}

/** Draws the sticker in sticker units (the caller sets the transform). */
function drawScene(ctx: CanvasRenderingContext2D, scene: Scene, selected: number | null) {
  const { pic, view, items, strokes, shape } = scene;
  ctx.save();
  if (shape === "circle") {
    ctx.beginPath();
    ctx.arc(S / 2, S / 2, S / 2, 0, Math.PI * 2);
    ctx.clip();
  } else if (shape === "rounded") {
    roundedPath(ctx, 0, 0, S, S, S * 0.22);
    ctx.clip();
  }
  const dest = destRect(pic, view);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  if (scene.outline && (scene.cut || shape !== "free")) {
    // Classic sticker border: the silhouette in white, nudged around the picture.
    for (let a = 0; a < 16; a++) {
      const t = (a * Math.PI) / 8;
      ctx.drawImage(pic.silhouette, dest.x + Math.cos(t) * OUTLINE_R, dest.y + Math.sin(t) * OUTLINE_R, dest.w, dest.h);
    }
  }
  ctx.drawImage(pic.base, dest.x, dest.y, dest.w, dest.h);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (const s of strokes) {
    if (s.points.length < 2) continue;
    ctx.strokeStyle = s.color;
    ctx.lineWidth = s.width;
    ctx.beginPath();
    ctx.moveTo(s.points[0]!, s.points[1]!);
    if (s.points.length === 2) ctx.lineTo(s.points[0]! + 0.1, s.points[1]!);
    for (let i = 2; i < s.points.length; i += 2) ctx.lineTo(s.points[i]!, s.points[i + 1]!);
    ctx.stroke();
  }
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  for (const it of items) {
    const { size, font } = itemFont(it);
    ctx.font = font;
    if (!it.emoji) {
      ctx.lineWidth = size / 7;
      ctx.strokeStyle = luminance(it.color) > 0.5 ? "#000000" : "#FFFFFF";
      ctx.strokeText(it.text, it.x, it.y);
      ctx.fillStyle = it.color;
    } else {
      ctx.fillStyle = "#FFFFFF";
    }
    ctx.fillText(it.text, it.x, it.y);
  }
  ctx.restore();
  if (selected !== null && items[selected]) {
    const b = itemBounds(ctx, items[selected]!);
    ctx.save();
    ctx.strokeStyle = "#4DA3FF";
    ctx.lineWidth = 2.5;
    roundedPath(ctx, b.x, b.y, b.w, b.h, 10);
    ctx.stroke();
    ctx.restore();
  }
}

function makeCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}

/** Push the editable pixels to the drawable canvases (picture + white silhouette). */
function syncPic(pic: Pic) {
  const bctx = pic.base.getContext("2d");
  const sctx = pic.silhouette.getContext("2d");
  if (!bctx || !sctx) return;
  bctx.putImageData(new ImageData(pic.rgba, pic.w, pic.h), 0, 0);
  sctx.globalCompositeOperation = "source-over";
  sctx.clearRect(0, 0, pic.w, pic.h);
  sctx.drawImage(pic.base, 0, 0);
  sctx.globalCompositeOperation = "source-in";
  sctx.fillStyle = "#FFFFFF";
  sctx.fillRect(0, 0, pic.w, pic.h);
  sctx.globalCompositeOperation = "source-over";
}

/* ------------------------------- dialogs --------------------------------- */

function askText(): Promise<string | null> {
  return openLayer<string | null>((close) => <TextDialog onDone={close} />, null);
}

function TextDialog({ onDone }: { onDone: (text: string | null) => void }) {
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const submit = () => onDone(value.trim() || null);
  return (
    <LayerDialog
      title="Add text"
      size="sm"
      onClose={() => onDone(null)}
      footer={
        <div className="sticker-dialog-actions">
          <button type="button" className="btn secondary small" onClick={() => onDone(null)}>
            Cancel
          </button>
          <button type="button" className="btn small" onClick={submit} disabled={!value.trim()}>
            Add
          </button>
        </div>
      }
    >
      <form
        className="field"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <input
          ref={inputRef}
          className="sticker-text-input"
          value={value}
          maxLength={40}
          aria-label="Sticker text"
          placeholder="Your text"
          enterKeyHint="done"
          onChange={(e) => setValue(e.target.value)}
        />
      </form>
    </LayerDialog>
  );
}

function askEmoji(): Promise<string | null> {
  return openLayer<string | null>(
    (close) => (
      <LayerDialog title="Add emoji" size="sheet" onClose={() => close(null)} className="sticker-emoji-sheet">
        <div className="sticker-emoji-grid" role="group" aria-label="Emoji">
          {EMOJI.map((e) => (
            <button key={e} type="button" className="emoji-cell" aria-label={`Add ${e}`} onClick={() => close(e)}>
              {e}
            </button>
          ))}
        </div>
      </LayerDialog>
    ),
    null,
  );
}

/* --------------------------------- icons --------------------------------- */

function ToolIcon({ name }: { name: string }) {
  const p = { stroke: "currentColor", strokeWidth: 1.75, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, fill: "none" };
  let body: ReactNode;
  switch (name) {
    case "cut":
      body = (
        <>
          <path d="m4 20 11-11" {...p} />
          <path d="m13 7 2-2 4 4-2 2z" {...p} />
          <path d="M6 4v3M4.5 5.5h3M19 14v3M17.5 15.5h3M10 3.5v1.5" {...p} />
        </>
      );
      break;
    case "move":
      body = <path d="M12 3v18M3 12h18M12 3l-3 3M12 3l3 3M12 21l-3-3M12 21l3-3M3 12l3-3M3 12l3 3M21 12l-3-3M21 12l-3 3" {...p} />;
      break;
    case "erase":
      body = (
        <>
          <path d="m8.5 20-5-5a2 2 0 0 1 0-2.8l8.7-8.7a2 2 0 0 1 2.8 0l5 5a2 2 0 0 1 0 2.8L13 20Z" {...p} />
          <path d="M8.5 20H20M7 9.5l7.5 7.5" {...p} />
        </>
      );
      break;
    case "restore":
      body = (
        <>
          <path d="M4 12a8 8 0 1 0 2.4-5.7L4 8.6" {...p} />
          <path d="M4 4v4.6h4.6" {...p} />
          <path d="M12 8v4l2.5 2.5" {...p} />
        </>
      );
      break;
    case "draw":
      body = (
        <>
          <path d="M15.5 4.5 19.5 8.5 9 19H5v-4Z" {...p} />
          <path d="m13 7 4 4" {...p} />
        </>
      );
      break;
    case "text":
      body = <path d="M5 6V4.5h14V6M12 4.5v15M9 19.5h6" {...p} />;
      break;
    case "shape-free":
      body = <path d="M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3" {...p} />;
      break;
    case "shape-circle":
      body = <circle cx="12" cy="12" r="8" {...p} />;
      break;
    case "shape-rounded":
      body = <rect x="4" y="4" width="16" height="16" rx="5" {...p} />;
      break;
    case "outline":
      body = (
        <>
          <rect x="3.5" y="3.5" width="17" height="17" rx="4" strokeDasharray="2.5 2.5" {...p} />
          <rect x="8" y="8" width="8" height="8" rx="2" {...p} />
        </>
      );
      break;
    case "undo":
      body = (
        <>
          <path d="M9 7 4.5 11.5 9 16" {...p} />
          <path d="M5 11.5h9.5a5 5 0 0 1 0 10H12" {...p} />
        </>
      );
      break;
    case "trash":
      body = <path d="M4.5 7h15M9.5 7V5h5v2M7 7l1 12h8l1-12M10.5 10.5v5M13.5 10.5v5" {...p} />;
      break;
    default:
      body = null;
  }
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" aria-hidden focusable="false">
      {body}
    </svg>
  );
}

/* --------------------------------- editor -------------------------------- */

export function StickerMaker({
  source,
  editing,
  onCancel,
  onDone,
}: {
  source: Blob;
  editing: boolean;
  onCancel: () => void;
  onDone: (png: Blob) => void;
}) {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [mode, setMode] = useState<Mode>("move");
  const [shape, setShape] = useState<Shape>("free");
  const [outline, setOutline] = useState(true);
  const [ink, setInk] = useState(INKS[0]!.color);
  const [brush, setBrush] = useState(BRUSH_DEFAULT);
  const [selected, setSelected] = useState<number | null>(null);
  const [cut, setCut] = useState(false);
  const [cutting, setCutting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [rev, setRev] = useState(0); // bumps when items / strokes are added or removed
  const [side, setSide] = useState(0);

  const pic = useRef<Pic | null>(null);
  const view = useRef<View>({ offX: 0, offY: 0, scale: 1 });
  const items = useRef<Item[]>([]);
  const strokes = useRef<Stroke[]>([]);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<Gesture>({ kind: "none" });
  const raf = useRef(0);
  const syncPending = useRef(false);
  const alive = useRef(true);
  const noticeTimer = useRef(0);

  // Latest UI state for the (stable) pointer handlers.
  const live = useRef({ mode, shape, outline, ink, brush, selected, cut });
  live.current = { mode, shape, outline, ink, brush, selected, cut };

  const finePointer = typeof window !== "undefined" && !!window.matchMedia?.("(pointer: fine)").matches;

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const p = pic.current;
    if (!canvas || !p) return;
    if (syncPending.current) {
      syncPending.current = false;
      syncPic(p);
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const k = canvas.width / S;
    ctx.setTransform(k, 0, 0, k, 0, 0);
    const st = live.current;
    drawScene(
      ctx,
      { pic: p, view: view.current, items: items.current, strokes: strokes.current, shape: st.shape, outline: st.outline, cut: st.cut },
      st.selected,
    );
  }, []);

  const requestDraw = useCallback(() => {
    if (raf.current) return;
    raf.current = window.requestAnimationFrame(() => {
      raf.current = 0;
      draw();
    });
  }, [draw]);

  const flash = useCallback((text: string) => {
    setNotice(text);
    window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setNotice(null), 4200);
  }, []);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      window.cancelAnimationFrame(raf.current);
      window.clearTimeout(noticeTimer.current);
    };
  }, []);

  // Load the picture (EXIF orientation, ≤768 px).
  useEffect(() => {
    let cancelled = false;
    loadPicture(source)
      .then((loaded) => {
        if (cancelled) return;
        const rgba = new Uint8ClampedArray(loaded.rgba);
        pic.current = {
          w: loaded.width,
          h: loaded.height,
          rgba,
          original: new Uint8ClampedArray(rgba),
          base: makeCanvas(loaded.width, loaded.height),
          silhouette: makeCanvas(loaded.width, loaded.height),
        };
        syncPic(pic.current);
        setCut(hasTransparency(rgba)); // an existing sticker counts as cut out
        setPhase("ready");
      })
      .catch(() => {
        if (!cancelled) setPhase("error");
      });
    return () => {
      cancelled = true;
    };
  }, [source]);

  // Canvas size follows the stage (CSS decides: ≤420 px, fits the viewport).
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || phase !== "ready") return;
    const measure = () => setSide(Math.round(stage.getBoundingClientRect().width));
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(stage);
    return () => ro.disconnect();
  }, [phase]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || side <= 0) return;
    const px = Math.round(side * Math.min(3, window.devicePixelRatio || 1));
    if (canvas.width !== px) {
      canvas.width = px;
      canvas.height = px;
    }
    draw();
  }, [side, draw]);

  useEffect(() => {
    requestDraw();
  }, [mode, shape, outline, selected, cut, rev, requestDraw]);

  /* ---- geometry helpers ---- */

  const toSticker = (e: { clientX: number; clientY: number }) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: ((e.clientX - rect.left) * S) / rect.width, y: ((e.clientY - rect.top) * S) / rect.height };
  };

  const hitItem = (x: number, y: number): number | null => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return null;
    for (let i = items.current.length - 1; i >= 0; i--) {
      const b = itemBounds(ctx, items.current[i]!);
      if (x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h) return i;
    }
    return null;
  };

  const paintAt = (x: number, y: number) => {
    const p = pic.current;
    if (!p) return;
    const dest = destRect(p, view.current);
    const k = p.w / dest.w;
    const touched = eraseDisc(
      p.rgba,
      p.w,
      p.h,
      (x - dest.x) * k,
      (y - dest.y) * k,
      (live.current.brush / 2) * k,
      live.current.mode === "restore" ? p.original : null,
    );
    if (touched) {
      syncPending.current = true;
      requestDraw();
    }
  };

  const paintLine = (x0: number, y0: number, x1: number, y1: number) => {
    const step = Math.max(1, live.current.brush * 0.25);
    const n = Math.max(1, Math.ceil(Math.hypot(x1 - x0, y1 - y0) / step));
    for (let i = 1; i <= n; i++) paintAt(x0 + ((x1 - x0) * i) / n, y0 + ((y1 - y0) * i) / n);
  };

  const zoomTarget = (target: number | "picture", factor: number) => {
    if (target === "picture") {
      view.current.scale = clamp(view.current.scale * factor, PICTURE_SCALE[0], PICTURE_SCALE[1]);
    } else {
      const it = items.current[target];
      if (it) it.scale = clamp(it.scale * factor, ITEM_SCALE[0], ITEM_SCALE[1]);
    }
    requestDraw();
  };

  const moveTarget = (target: number | "picture", dx: number, dy: number) => {
    if (target === "picture") {
      view.current.offX += dx;
      view.current.offY += dy;
    } else {
      const it = items.current[target];
      if (it) {
        it.x = clamp(it.x + dx, 0, S);
        it.y = clamp(it.y + dy, 0, S);
      }
    }
    requestDraw();
  };

  /* ---- pointer gestures (mouse, pen, touch; two pointers pinch) ---- */

  const onPointerDown = (e: PointerEvent<HTMLCanvasElement>) => {
    if (phase !== "ready" || cutting) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const canvas = e.currentTarget;
    try {
      canvas.setPointerCapture(e.pointerId); // keep the drag when it leaves the canvas
    } catch {
      /* pointer already gone (or synthetic): the gesture still works without capture */
    }
    canvas.focus({ preventScroll: true });
    const p = toSticker(e);
    pointers.current.set(e.pointerId, p);
    const st = live.current;
    if (pointers.current.size === 1) {
      const hit = hitItem(p.x, p.y);
      if (hit !== null) {
        setSelected(hit);
        setMode("move");
        gesture.current = { kind: "item", index: hit, lastX: p.x, lastY: p.y, startX: p.x, startY: p.y, moved: false };
      } else if (st.mode === "move") {
        gesture.current = { kind: "pan", lastX: p.x, lastY: p.y, startX: p.x, startY: p.y, moved: false };
      } else if (st.mode === "draw") {
        const stroke: Stroke = { color: st.ink, width: st.brush / 3, points: [p.x, p.y] };
        strokes.current.push(stroke);
        setRev((r) => r + 1);
        gesture.current = { kind: "draw", stroke };
      } else {
        paintAt(p.x, p.y);
        gesture.current = { kind: "paint", lastX: p.x, lastY: p.y };
      }
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const g = gesture.current;
      const target: number | "picture" = g.kind === "item" ? g.index : "picture";
      gesture.current = {
        kind: "pinch",
        target,
        startDist: Math.max(1, Math.hypot(b!.x - a!.x, b!.y - a!.y)),
        startScale: target === "picture" ? view.current.scale : (items.current[target]?.scale ?? 1),
        midX: (a!.x + b!.x) / 2,
        midY: (a!.y + b!.y) / 2,
      };
    }
  };

  const onPointerMove = (e: PointerEvent<HTMLCanvasElement>) => {
    if (!pointers.current.has(e.pointerId)) return;
    const p = toSticker(e);
    pointers.current.set(e.pointerId, p);
    const g = gesture.current;
    switch (g.kind) {
      case "pan":
      case "item": {
        moveTarget(g.kind === "pan" ? "picture" : g.index, p.x - g.lastX, p.y - g.lastY);
        g.lastX = p.x;
        g.lastY = p.y;
        if (Math.hypot(p.x - g.startX, p.y - g.startY) > 4) g.moved = true;
        break;
      }
      case "draw": {
        const pts = g.stroke.points;
        const lx = pts[pts.length - 2]!;
        const ly = pts[pts.length - 1]!;
        if (Math.hypot(p.x - lx, p.y - ly) >= 0.75) {
          pts.push(p.x, p.y);
          requestDraw();
        }
        break;
      }
      case "paint":
        paintLine(g.lastX, g.lastY, p.x, p.y);
        g.lastX = p.x;
        g.lastY = p.y;
        break;
      case "pinch": {
        const [a, b] = [...pointers.current.values()];
        if (!a || !b) break;
        const dist = Math.max(1, Math.hypot(b.x - a.x, b.y - a.y));
        const midX = (a.x + b.x) / 2;
        const midY = (a.y + b.y) / 2;
        const range = g.target === "picture" ? PICTURE_SCALE : ITEM_SCALE;
        const scale = clamp((g.startScale * dist) / g.startDist, range[0], range[1]);
        if (g.target === "picture") view.current.scale = scale;
        else if (items.current[g.target]) items.current[g.target]!.scale = scale;
        moveTarget(g.target, midX - g.midX, midY - g.midY);
        g.midX = midX;
        g.midY = midY;
        break;
      }
      default:
        break;
    }
  };

  const onPointerEnd = (e: PointerEvent<HTMLCanvasElement>) => {
    if (!pointers.current.delete(e.pointerId)) return;
    const g = gesture.current;
    // A tap on the picture deselects (like the phone).
    if (g.kind === "pan" && !g.moved && pointers.current.size === 0) setSelected(null);
    gesture.current = { kind: "none" };
  };

  // Mouse wheel / trackpad zoom (needs a non-passive listener to keep the page still).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || phase !== "ready") return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const unit = e.deltaMode === 1 ? 0.05 : 0.0015;
      const sel = live.current.selected;
      zoomTarget(sel !== null && live.current.mode === "move" ? sel : "picture", Math.exp(-e.deltaY * unit));
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", onWheel);
    // zoomTarget only touches refs, so binding once per phase is enough.
  }, [phase]);

  /* ---- actions ---- */

  const cutOut = async () => {
    const p = pic.current;
    if (!p || cutting) return;
    setCutting(true);
    try {
      const { rgba, share } = await runCutout(p.original, p.w, p.h);
      if (!alive.current) return;
      if (cutoutLooksWrong(share)) {
        flash(CUTOUT_FAILED_MESSAGE);
        return;
      }
      p.rgba = new Uint8ClampedArray(rgba);
      syncPic(p);
      const fit = fitToSubject(p.rgba, p.w, p.h, S);
      if (fit) view.current = { offX: fit.offsetX, offY: fit.offsetY, scale: fit.scale };
      navigator.vibrate?.(18);
      setCut(true);
      requestDraw();
    } catch {
      if (alive.current) flash(CUTOUT_FAILED_MESSAGE);
    } finally {
      if (alive.current) setCutting(false);
    }
  };

  const addText = async () => {
    const text = await askText();
    if (!text || !alive.current) return;
    items.current.push({ text, emoji: false, color: live.current.ink, x: 0.5 * S, y: 0.82 * S, scale: 1 });
    setSelected(items.current.length - 1);
    setMode("move");
    setRev((r) => r + 1);
  };

  const addEmoji = async () => {
    const pick = await askEmoji();
    if (!pick || !alive.current) return;
    items.current.push({ text: pick, emoji: true, color: "#FFFFFF", x: 0.78 * S, y: 0.22 * S, scale: 1 });
    setSelected(items.current.length - 1);
    setMode("move");
    setRev((r) => r + 1);
  };

  const removeSelected = () => {
    if (selected === null) return;
    items.current.splice(selected, 1);
    setSelected(null);
    setRev((r) => r + 1);
  };

  const canUndo = items.current.length > 0 || strokes.current.length > 0;
  const undo = () => {
    // Same order as the phone: strokes first while drawing (or when there are no items).
    if (strokes.current.length > 0 && (mode === "draw" || items.current.length === 0)) {
      strokes.current.pop();
    } else if (items.current.length > 0) {
      items.current.pop();
      setSelected(null);
    } else if (strokes.current.length > 0) {
      strokes.current.pop();
    }
    setRev((r) => r + 1);
  };

  /** Brush tools drop the text / emoji selection, so a colour pick can't recolour it by surprise. */
  const pickMode = (next: Mode) => {
    setMode(next);
    if (next !== "move") setSelected(null);
  };

  const pickInk = (color: string) => {
    setInk(color);
    const it = selected !== null ? items.current[selected] : undefined;
    if (it && !it.emoji) {
      it.color = color;
      requestDraw();
    }
  };

  const done = async () => {
    const p = pic.current;
    if (!p || saving) return;
    setSelected(null);
    setSaving(true);
    if (syncPending.current) {
      syncPending.current = false;
      syncPic(p); // last eraser strokes not painted yet
    }
    try {
      const out = makeCanvas(S, S);
      const ctx = out.getContext("2d");
      if (!ctx) throw new Error("Couldn't make the sticker.");
      drawScene(
        ctx,
        { pic: p, view: view.current, items: items.current, strokes: strokes.current, shape, outline, cut },
        null,
      );
      const png = await encodeSticker(out);
      if (alive.current) onDone(png);
    } catch (e) {
      if (alive.current) flash(e instanceof Error ? e.message : "Couldn't make the sticker.");
    } finally {
      if (alive.current) setSaving(false);
    }
  };

  const onCanvasKey = (e: KeyboardEvent<HTMLCanvasElement>) => {
    const target: number | "picture" = selected !== null ? selected : "picture";
    const step = e.shiftKey ? 32 : 8;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    if (moves[e.key]) {
      e.preventDefault();
      moveTarget(target, moves[e.key]![0], moves[e.key]![1]);
    } else if (e.key === "+" || e.key === "=") {
      e.preventDefault();
      zoomTarget(target, 1.1);
    } else if (e.key === "-" || e.key === "_") {
      e.preventDefault();
      zoomTarget(target, 1 / 1.1);
    } else if ((e.key === "Delete" || e.key === "Backspace") && selected !== null) {
      e.preventDefault();
      removeSelected();
    } else if (e.key === "Escape" && selected !== null) {
      e.preventDefault();
      e.stopPropagation(); // deselect first; a second Escape closes the maker
      setSelected(null);
    }
  };

  const onRootKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "z" && canUndo) {
      e.preventDefault();
      undo();
    }
  };

  const hint =
    mode === "move"
      ? selected !== null
        ? "Drag or pinch the text / emoji. Tap the picture to deselect."
        : `Drag and pinch to place the picture.${finePointer ? " Scroll to zoom." : ""}`
      : mode === "erase"
        ? "Rub out what shouldn't be in the sticker."
        : mode === "restore"
          ? "Rub to bring parts of the picture back."
          : "Draw on the sticker.";

  const showInkRow = mode !== "move" || selected !== null;
  const ready = phase === "ready";

  const tool = (
    key: string,
    icon: string,
    label: string,
    onClick: () => void,
    opts: { on?: boolean; pressed?: boolean; disabled?: boolean } = {},
  ) => (
    <button
      key={key}
      type="button"
      className={`sticker-tool${opts.on ? " on" : ""}`}
      onClick={onClick}
      disabled={!ready || opts.disabled}
      aria-pressed={opts.pressed}
      data-testid={`sticker-tool-${key}`}
    >
      <ToolIcon name={icon} />
      <span>{label}</span>
    </button>
  );

  return (
    <LayerDialog
      title={editing ? "Edit sticker" : "New sticker"}
      onClose={onCancel}
      className="sticker-maker-card"
      backdropClassName="sticker-maker-backdrop"
      headerActions={
        <>
          <button
            type="button"
            className="icon-btn"
            aria-label="Undo"
            title="Undo"
            onClick={undo}
            disabled={!ready || !canUndo}
          >
            <ToolIcon name="undo" />
          </button>
          <button
            type="button"
            className="btn small sticker-done"
            data-testid="sticker-done"
            onClick={() => void done()}
            disabled={!ready || cutting || saving}
          >
            {saving ? "Saving…" : "Done"}
          </button>
        </>
      }
      footer={
        <div className="sticker-toolbar" role="toolbar" aria-label="Sticker tools">
          {cutting ? (
            <div className="sticker-tool sticker-tool-busy" role="status" aria-live="polite">
              <span className="sticker-spinner" aria-hidden />
              <span>Cutting…</span>
            </div>
          ) : (
            tool("cutout", "cut", cut ? "Cut again" : "Cut out", () => void cutOut())
          )}
          {tool("move", "move", "Move", () => pickMode("move"), { on: mode === "move", pressed: mode === "move" })}
          {tool("erase", "erase", "Erase", () => pickMode("erase"), { on: mode === "erase", pressed: mode === "erase" })}
          {tool("restore", "restore", "Restore", () => pickMode("restore"), { on: mode === "restore", pressed: mode === "restore" })}
          {tool("draw", "draw", "Draw", () => pickMode("draw"), { on: mode === "draw", pressed: mode === "draw" })}
          {tool("text", "text", "Text", () => void addText())}
          <button
            type="button"
            className="sticker-tool"
            onClick={() => void addEmoji()}
            disabled={!ready}
            data-testid="sticker-tool-emoji"
          >
            <IconEmoji size={22} />
            <span>Emoji</span>
          </button>
          {tool("shape", `shape-${shape}`, SHAPE_LABEL[shape], () => setShape(NEXT_SHAPE[shape]), { on: shape !== "free" })}
          {tool("outline", "outline", "Outline", () => setOutline((v) => !v), { on: outline, pressed: outline })}
        </div>
      }
    >
      <div className="sticker-maker" onKeyDown={onRootKey}>
        {phase === "loading" && (
          <div className="sticker-maker-loading" role="status">
            <span className="sticker-spinner" aria-hidden />
            <span className="muted">Opening picture…</span>
          </div>
        )}
        {phase === "error" && (
          <div className="sticker-maker-loading" role="alert">
            <span>{OPEN_FAILED_MESSAGE}</span>
            <button type="button" className="btn secondary small" onClick={onCancel}>
              Close
            </button>
          </div>
        )}
        {ready && (
          <>
            <div ref={stageRef} className={`sticker-stage${cutting ? " busy" : ""}`}>
              <canvas
                ref={canvasRef}
                className={`sticker-canvas mode-${mode}`}
                tabIndex={0}
                role="application"
                aria-roledescription="sticker canvas"
                aria-label="Sticker canvas. Arrow keys move the picture or the selected text or emoji, plus and minus zoom, Delete removes the selected item."
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerEnd}
                onPointerCancel={onPointerEnd}
                onLostPointerCapture={onPointerEnd}
                onKeyDown={onCanvasKey}
                onContextMenu={(e) => e.preventDefault()}
              />
            </div>
            <p className="sticker-hint">{hint}</p>
            {/* Always laid out (fixed height) so the dialog doesn't jump between tools. */}
            <div className="sticker-ink-row">
              {showInkRow && (mode === "draw" || selected !== null) && (
                <div className="sticker-inks" role="group" aria-label="Colour">
                  {INKS.map((c) => (
                    <button
                      key={c.color}
                      type="button"
                      className={`sticker-ink${ink === c.color ? " on" : ""}`}
                      style={{ background: c.color }}
                      aria-label={c.name}
                      aria-pressed={ink === c.color}
                      title={c.name}
                      onClick={() => pickInk(c.color)}
                    />
                  ))}
                </div>
              )}
              {mode !== "move" && (
                <input
                  type="range"
                  className="sticker-brush"
                  min={BRUSH_MIN}
                  max={BRUSH_MAX}
                  value={brush}
                  aria-label="Brush size"
                  title="Brush size"
                  onChange={(e) => setBrush(Number(e.target.value))}
                />
              )}
              {selected !== null && mode === "move" && (
                <button
                  type="button"
                  className="icon-btn sticker-remove-item"
                  aria-label="Remove text or emoji"
                  title="Remove"
                  onClick={removeSelected}
                >
                  <ToolIcon name="trash" />
                </button>
              )}
            </div>
          </>
        )}
        <p className="sticker-maker-status" role="status" aria-live="polite">
          {notice ?? ""}
        </p>
      </div>
    </LayerDialog>
  );
}
